import API from "api";
import Session from "helpers/session";
import { exportLink } from "helpers/variables";
import {
  buildMetadataHeader,
  buildExportFilename,
  combineMemberExport,
  formatNotesSection,
  formatNotificationsSection,
  formatReadingsSection,
} from "views/PatientBoard/microViews/Export/ExportFormatting";
import { VITALS } from "views/PatientBoard/microViews/Export/Export.logic";

export interface Includes {
  type: string;
  delivery: string;
  notifications: boolean;
  notes: boolean;
  bgl: boolean;
  bp: boolean;
  hrt: boolean;
  spo2: boolean;
  spir: boolean;
  temp: boolean;
  wt: boolean;
  activity: boolean;
  sleep: boolean;
  pain: boolean;
  oneCSV: boolean;
}

const exportOneMember = async (
  includes: Includes,
  start: string,
  end: string,
  token: string,
  memberId: string
) => {
  const tasks: Promise<{ kind: string; vital?: string; csv: string }>[] = [];

  VITALS.forEach((vital) => {
    if (includes[vital as keyof Includes]) {
      tasks.push(
        API.kore.readings
          .getForOne(
            memberId,
            token,
            vital,
            `&format=csv&measured_between=${start},${end}`
          )
          .then((csv) => ({ kind: "reading", vital, csv }))
      );
    }
  });

  if (includes.notes) {
    tasks.push(
      API.kore.notes
        .getCSV(
          Session.crackCookieToken("coordinator"),
          `memberId:${memberId}&format=csv&created_between=${start},${end}`
        )
        .then((csv) => ({ kind: "notes", csv }))
    );
  }

  if (includes.notifications) {
    tasks.push(
      API.kore.notifications
        .getCSV(
          Session.crackCookieToken("coordinator"),
          `memberId:${memberId}&format=csv`
        )
        .then((csv) => ({ kind: "notifications", csv }))
    );
  }

  const results = await Promise.all(tasks);

  if (includes.oneCSV) {
    handleCombined(memberId, start, end, results);
  } else {
    handleMulti(memberId, start, end, results);
  }
};

const handleCombined = (
  memberId: string,
  start: string,
  end: string,
  results: { kind: string; vital?: string; csv: string }[]
) => {
  const sections: (string | null)[] = [];
  const includesList: string[] = [];

  results.forEach(({ kind, vital, csv }) => {
    let section: string | null = null;
    if (kind === "reading" && vital) {
      section = formatReadingsSection(vital, csv);
      if (section) includesList.push(vital);
    } else if (kind === "notes") {
      section = formatNotesSection(csv);
      if (section) includesList.push("notes");
    } else if (kind === "notifications") {
      section = formatNotificationsSection(csv);
      if (section) includesList.push("notifications");
    }
    if (section) sections.push(section);
  });

  if (sections.length === 0) return;

  const header = buildMetadataHeader(memberId, start, end, includesList);
  const finalCSV = combineMemberExport(header, sections);
  const filename = buildExportFilename(memberId, start, end);
  exportLink(finalCSV, filename, "text/csv");
};

const handleMulti = (
  memberId: string,
  start: string,
  end: string,
  results: { kind: string; vital?: string; csv: string }[]
) => {
  results.forEach(({ kind, vital, csv }) => {
    let section: string | null = null;
    let dataType: string = "";

    if (kind === "reading" && vital) {
      section = formatReadingsSection(vital, csv);
      dataType = vital;
    } else if (kind === "notes") {
      section = formatNotesSection(csv);
      dataType = "notes";
    } else if (kind === "notifications") {
      section = formatNotificationsSection(csv);
      dataType = "notifications";
    }

    if (!section) return;

    const header = buildMetadataHeader(memberId, start, end, [dataType]);
    const finalCSV = combineMemberExport(header, [section]);
    const filename = buildExportFilename(memberId, start, end, dataType);
    exportLink(finalCSV, filename, "text/csv");
  });
};

export const submitExport = async (
  includes: Includes,
  start: Date,
  end: Date,
  token: string,
  memberIds: string[]
) => {
  if (includes.type !== "csv") return;
  if (!start || !end) return;

  const startStr = start.toISOString();
  const endStr = end.toISOString();

  for (const memberId of memberIds) {
    await exportOneMember(includes, startStr, endStr, token, memberId);
  }
};